import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  styled,
  keyframes,
} from '@mui/material';
import { startupMarketplaceAPI } from '../../utils/api';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled(Box)({
  background: '#FFFFFF',
  position: 'relative',
  padding: '40px 20px 120px', // renders inside the shell main area
  '@media (max-width: 600px)': {
    padding: '24px 16px 80px',
  },
});

const ContentWrapper = styled(Box)({
  maxWidth: '560px',
  margin: '0 auto',
  animation: `${fadeIn} 0.8s cubic-bezier(0.16, 1, 0.3, 1)`,
});

const Hero = styled(Typography)({
  fontSize: '48px',
  fontWeight: 600,
  color: '#1D1D1F',
  lineHeight: 1.08,
  letterSpacing: '-0.02em',
  marginBottom: '16px',
  textAlign: 'center',
  '@media (max-width: 600px)': {
    fontSize: '40px',
  },
});

const Subhead = styled(Typography)({
  fontSize: '21px',
  fontWeight: 400,
  color: '#6E6E73',
  lineHeight: 1.38,
  letterSpacing: '0.011em',
  textAlign: 'center',
  marginBottom: '56px',
  '@media (max-width: 600px)': {
    fontSize: '19px',
    marginBottom: '40px',
  },
});

const StyledInput = styled(TextField)({
  width: '100%',
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    background: '#F5F5F7',
    borderRadius: '12px',
    fontSize: '17px',
    fontWeight: 400,
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      background: '#EBEBED',
    },
    '&.Mui-focused': {
      background: '#FFFFFF',
      boxShadow: '0 0 0 4px rgba(0, 0, 0, 0.08)',
    },
  },
  '& input, & textarea': {
    padding: '18px 20px',
    '&::placeholder': {
      color: '#86868B',
      opacity: 1,
    },
  },
});

const PrimaryButton = styled(Box)(({ disabled }) => ({
  background: disabled ? '#EBEBED' : '#000000',
  color: '#FFFFFF',
  padding: '18px 56px',
  borderRadius: '980px',
  fontSize: '17px',
  fontWeight: 500,
  letterSpacing: '-0.02em',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  textAlign: 'center',
  width: '100%',
  opacity: disabled ? 0.5 : 1,
  marginBottom: '12px',
  '@media (max-width: 600px)': {
    padding: '18px 32px',
  },
  '&:hover': {
    opacity: disabled ? 0.5 : 0.85,
    transform: disabled ? 'none' : 'translateY(-2px)',
  },
  '&:active': {
    transform: disabled ? 'none' : 'translateY(0)',
  },
}));

const SecondaryButton = styled(Box)({
  background: 'transparent',
  color: '#1D1D1F',
  padding: '18px 56px',
  borderRadius: '980px',
  fontSize: '17px',
  fontWeight: 500,
  letterSpacing: '-0.02em',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  textAlign: 'center',
  width: '100%',
  marginBottom: '12px',
  '@media (max-width: 600px)': {
    padding: '18px 32px',
  },
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.04)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
});

const SectionLabel = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#1D1D1F',
  marginBottom: '12px',
  letterSpacing: '-0.01em',
});

const CheckboxRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '24px',
});

const StartupEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startup, setStartup] = useState(null);
  const [bylaws, setBylaws] = useState('');
  const [joinLink, setJoinLink] = useState('');
  const [buyIn, setBuyIn] = useState('');
  const [description, setDescription] = useState('');
  const [yearsPaid, setYearsPaid] = useState([]);
  const [escrowType, setEscrowType] = useState('');
  const [escrowDetails, setEscrowDetails] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStartup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchStartup = async () => {
    try {
      setLoading(true);
      const response = await startupMarketplaceAPI.getById(id);
      const data = response.data;
      setStartup(data);

      // Pre-populate form
      setBylaws(data.bylaws || '');
      setJoinLink(data.joinLink || '');
      setBuyIn(data.buyIn ? data.buyIn.toString() : '');
      setDescription(data.description || '');
      setYearsPaid(data.yearsPaid || []);
      setEscrowType(data.escrowType || '');
      setEscrowDetails(data.escrowDetails || '');
    } catch (err) {
      setError('Failed to load startup listing');
      console.error('Failed to fetch startup:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await startupMarketplaceAPI.update(id, {
        bylaws: bylaws || null,
        joinLink: joinLink || null,
        buyIn: buyIn ? parseFloat(buyIn.replace(/,/g, '')) : null,
        description,
        yearsPaid,
        escrowType: escrowType || null,
        escrowDetails: escrowDetails || null,
      });

      navigate(`/startup-marketplace/${id}`, {
        state: { message: 'Listing updated successfully' },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  const handleBuyInChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setBuyIn(value);
  };

  const formatBuyIn = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const toggleYearPaid = (year) => {
    setYearsPaid((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year].sort()
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress sx={{ color: '#1D1D1F' }} size={40} thickness={2} />
        </Box>
      </PageContainer>
    );
  }

  if (!startup) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography
            sx={{
              color: '#FF3B30',
              fontSize: '17px',
              fontWeight: 400,
              textAlign: 'center',
            }}
          >
            Startup listing not found
          </Typography>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Hero>Edit your listing.</Hero>
        <Subhead>Update the details for {startup.leagueName}.</Subhead>

        <StyledInput
          placeholder="https://sleeper.com/leagues/..."
          value={joinLink}
          onChange={(e) => setJoinLink(e.target.value)}
          disabled={saving}
          helperText="Sleeper join link"
          FormHelperTextProps={{
            sx: {
              fontSize: '14px',
              color: '#86868B',
              marginLeft: '4px',
              marginTop: '8px',
            },
          }}
        />

        <StyledInput
          placeholder="https://docs.google.com/..."
          value={bylaws}
          onChange={(e) => setBylaws(e.target.value)}
          disabled={saving}
          helperText="League bylaws URL (optional)"
          FormHelperTextProps={{
            sx: {
              fontSize: '14px',
              color: '#86868B',
              marginLeft: '4px',
              marginTop: '8px',
            },
          }}
        />

        <StyledInput
          placeholder="0"
          value={formatBuyIn(buyIn)}
          onChange={handleBuyInChange}
          disabled={saving}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontSize: '17px', fontWeight: 400, color: '#1D1D1F' }}>$</Typography>
              </InputAdornment>
            ),
          }}
          helperText="Annual buy-in amount"
          FormHelperTextProps={{
            sx: {
              fontSize: '14px',
              color: '#86868B',
              marginLeft: '4px',
              marginTop: '8px',
            },
          }}
        />

        <Box sx={{ marginBottom: '20px' }}>
          <SectionLabel>Years paid (optional)</SectionLabel>
          <CheckboxRow>
            {[2026, 2027, 2028, 2029, 2030].map((year) => (
              <FormControlLabel
                key={year}
                control={
                  <Checkbox
                    checked={yearsPaid.includes(year)}
                    onChange={() => toggleYearPaid(year)}
                    disabled={saving}
                    sx={{
                      color: '#86868B',
                      '&.Mui-checked': {
                        color: '#1D1D1F',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '15px', fontWeight: 400, color: '#1D1D1F' }}>
                    {year}
                  </Typography>
                }
              />
            ))}
          </CheckboxRow>
        </Box>

        <Box sx={{ marginBottom: '20px' }}>
          <SectionLabel>Escrow (optional)</SectionLabel>
          <CheckboxRow>
            {['leaguesafe', 'teamstake', 'other', 'none'].map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={escrowType === type || (type === 'none' && !escrowType)}
                    onChange={() => {
                      if (type === 'none') {
                        setEscrowType('');
                        setEscrowDetails('');
                      } else {
                        setEscrowType(type);
                        if (type !== 'other') {
                          setEscrowDetails('');
                        }
                      }
                    }}
                    disabled={saving}
                    sx={{
                      color: '#86868B',
                      '&.Mui-checked': {
                        color: '#1D1D1F',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '15px', fontWeight: 400, color: '#1D1D1F' }}>
                    {type === 'leaguesafe'
                      ? 'LeagueSafe'
                      : type === 'teamstake'
                      ? 'TeamStake'
                      : type === 'other'
                      ? 'Other'
                      : 'None'}
                  </Typography>
                }
              />
            ))}
          </CheckboxRow>
        </Box>

        {escrowType === 'other' && (
          <StyledInput
            placeholder="Escrow details (e.g., name of service)"
            value={escrowDetails}
            onChange={(e) => setEscrowDetails(e.target.value)}
            disabled={saving}
          />
        )}

        <StyledInput
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={4}
          disabled={saving}
        />

        {error && (
          <Typography sx={{ color: '#FF3B30', fontSize: '15px', marginBottom: '16px', textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        <PrimaryButton onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save changes'}
        </PrimaryButton>
        <SecondaryButton onClick={() => navigate(`/startup-marketplace/${id}`)}>Back</SecondaryButton>
      </ContentWrapper>
    </PageContainer>
  );
};

export default StartupEdit;
